import { Injectable } from '@angular/core';
import { User } from '@mitel/cloudlink-sdk';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { AccountsService } from 'src/app/core/services/accounts.service';
import { AllMitelProducts, GwpProducts, MitelBundledProducts } from '../../../core/constants/product-licenses';
import { ProductLicense } from '../../../core/interfaces/product-licenses-interfaces';

@Injectable({
  providedIn: 'root',
})
export class ProductLicensesService {
  constructor(
    private translateService: TranslateService,
    private accountsService: AccountsService
  ) { }

  getMitelUserProductList() {
    let completeList: ProductLicense[] = [];
    MitelBundledProducts.forEach((element) => {
      let newItem = Object.assign({}, element);

      if (element.learnMoreLink) {
        newItem.learnMoreLink = this.translateService.instant(element.learnMoreLink);
      }
      completeList.push(newItem);
    });
    return completeList;
  }

  /**
   * Gets and iterates over products of `MitelBundledProducts`, returning an array of all
   * products which have the required account integrations and feature flags.
   * @returns Returns the new array of `ProductLicense` products which have the required account integrations and feature flags.
   */
  async getAvailableMitelUserProductList(): Promise<ProductLicense[]> {
    let completeList: ProductLicense[] = [];
    let productTags: string[];
    let accountId = this.accountsService.getCurrentAccount()?.accountId;

    await this.accountsService.getAccountTagsById(accountId)
      .then((tags: any) => {
        productTags = tags.products ? tags.products : [];
      })
      .catch(() => {
        productTags = [];
      });

    for await (const product of MitelBundledProducts) {
      let newItem = _.cloneDeep(product);
      let hasRequiredAccountIntegrations = await newItem.service.hasRequiredAccountIntegrations({ accountId: accountId } as User, productTags);
      let canBeSelected = newItem.service.canBeSelected ? await newItem.service.canBeSelected({ accountId: accountId } as User) : true;
      if (hasRequiredAccountIntegrations && canBeSelected) {
        if (product.learnMoreLink) {
          newItem.learnMoreLink = this.translateService.instant(product.learnMoreLink);
        }
        completeList.push(newItem);
      }
    }

    return completeList;
  }


  /**
   * Gets and iterates over products of `MitelBundledProducts`, returning an array of all
   * products which have the required account integrations and feature flags, and which
   * `asyncPredicate` resolves truthy for. The asyncPredicate is invoked with two
   * arguments: (user, product).
   * 
   * @param {Function} asyncPredicate The function invoked per iteration.
   * @returns Returns the new filtered array of `ProductLicense` products which have the required account integrations and feature flags.
   * @see getAvailableMitelUserProductList
   * @example
   * 
   * const requiresConfiguration = (user, product) => Promise.resolve(product.requiresConfiguration);
   * getAvailableMitelUserProductListAndFilter(requiresConfiguration);
   * // => ProductLicense objects with requiresConfiguration == true
   *
   * const canBeTemplated = (user, product) => product.service.canBeTemplated();
   * getAvailableMitelUserProductListAndFilter(canBeTemplated);
   * // => ProductLicense objects that can be templated
   * 
   */
  async getMitelUserProductListAndFilter(asyncPredicate: (user, product: ProductLicense) => Promise<boolean>): Promise<ProductLicense[]> {
    let completeList: ProductLicense[] = [];
    let productTags: string[];
    let accountId = this.accountsService.getCurrentAccount()?.accountId;

    await this.accountsService.getAccountTagsById(accountId)
      .then((tags: any) => {
        productTags = tags.products ? tags.products : [];
      })
      .catch(() => {
        productTags = [];
      });

    for await (const product of MitelBundledProducts) {
      let newItem = _.cloneDeep(product);
      let hasRequiredAccountIntegrations = await newItem.service.hasRequiredAccountIntegrations({ accountId: accountId } as User, productTags);
      let predicateResponse = !!asyncPredicate ? await asyncPredicate({ accountId: accountId } as User, newItem) : true;
      if (hasRequiredAccountIntegrations && predicateResponse) {
        if (product.learnMoreLink) {
          newItem.learnMoreLink = this.translateService.instant(product.learnMoreLink);
        }
        completeList.push(newItem);
      }
    }

    return completeList;
  }

  getMitelUserProduct(prodName: string) {
    let sProd = AllMitelProducts.find((element) => {
      return prodName === element.productCode;
    });
    if (sProd) {
      let sProdDesc = Object.assign({}, sProd);
      if (sProdDesc.learnMoreLink) {
        sProdDesc.learnMoreLink = this.translateService.instant(sProdDesc.learnMoreLink);
      }
      return sProdDesc;
    }
    return null;
  }

  getGWPUserProduct(prodName: string) {
    let prod = GwpProducts.find((element) => {
      return prodName === element.productCode;
    });
    if (prod) {
      let prodDesc = Object.assign({}, prod);
      if (prodDesc.learnMoreLink) {
        prodDesc.learnMoreLink = this.translateService.instant(prodDesc.learnMoreLink);
      }
      return prodDesc;
    }
    return null;
  }
}
